/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { buttonSize } from '../../registry/constants';
import { defaultValue } from './Rating';

const RatingManager: ComponentManagerFn<'Rating'> = (
  platform: Platform,
): ComponentManager<'Rating'> => {
  return {
    ...baseComponentManager<'Rating'>(platform, 'Rating'),
    type: 'Rating',
    name: 'Rating',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
      },
      numberOfStars: {
        label: 'Max Rating',
        type: 'number',
        min: 3,
        max: 10,
      },
      precision: {
        label: 'Allow Semi Rating',
        type: 'boolean',
        helpText:
          'Check to allow the rating with increments of 0.5 (half star) instead of a full star.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        required: true,
        getAttributeDataTypes: () => ['Integer', 'Decimal', 'Double'],
        preventMultiEdit: true,
      },
      ...toolTipProps,
    },
    category: 'Forms',
    getDisplayText: (item) => item.props.viewAttribute,
    icon: { iconName: 'star' },
    description: `**Overview**<br/>
The Rating component provides a visual interface for users to provide feedback using a star-based scoring system. Instead of typing a number (like "4"), users click on star icons to indicate their sentiment.
Use this component in feedback forms, product reviews, or satisfaction surveys where a quick, intuitive "score" is needed.

**1. Data Binding**<br/>
This component binds to numeric columns in your Data Store to save the rating value.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** **Integer**, **Decimal**, **Double**.

**2. Configuration**<br/>
You can customize the scale and precision of the rating system.
* **Max Rating (numberOfStars):** Sets the total number of stars displayed. (Range: Minimum 3, Maximum 10).
* **Allow Semi Rating (precision):** A boolean toggle.
    * **Checked:** Users can select half-stars (e.g., 3.5, 4.5).
    * **Unchecked:** Users can only select full integer values (e.g., 1, 2, 3, 4, 5).

**3. Visual Styling**<br/>
* **Color:** Sets the color of the "active" (filled) stars using the theme palette (e.g., **primary**, **secondary**).
* **Size:** Controls the physical size of the star icons (Small, Medium, Large).
* **Tooltip:** Supports standard tooltip properties to guide users (e.g., "1 = Poor, 5 = Excellent").

**4. Interactivity**<br/>
* **TriggerOnChange:** Supports the **TriggerOnChange** child component.
* **Use Case:** Automatically submitting a feedback form as soon as the user clicks a star, or triggering a conditional text box if a low rating is selected.

**5. Best Use Cases**<br/>
* **Product Reviews:** Allowing customers to rate a purchase on a 1-5 scale.
* **Service Feedback:** Asking "How was your support experience?" after a chat session.
* **Skill Assessment:** Self-rating proficiency in a skill (e.g., "Java: 4/5 Stars").
* **NPS Surveys:** Using a 1-10 star scale for "Likelihood to Recommend."`,
  };
};

export default RatingManager;
